/**
 * What is selected, where its handles are, and what a drag does to it (SRS C4).
 *
 * A stroke is frozen once drawn (docs/05-DATA-MODEL.md §3.1), so nothing here edits
 * one. Moving, resizing and rotating all produce a new record carrying the same id,
 * built from the stroke as it was when the drag began — which is why dragging a long
 * way and coming back leaves the writing exactly as it started rather than slowly
 * losing its shape to rounding.
 */

import { newStrokeId, type BoardObject, type Point, type Rect, type Stroke } from '@opal/shared';

import {
  applyToPoint,
  boundsOfRects,
  multiply,
  pointInPolygon,
  pointInRect,
  rotationAbout,
  scaleAbout,
  scaleFactorOf,
  translation,
  type Matrix,
} from './geometry.js';
import { strokePointsOf } from './stroke-bounds.js';

/** SRS C4 — the lasso is freehand; the rectangle is a dragged box. */
export type SelectMode = 'lasso' | 'rect';

/**
 * One piece of the board, held away from it — what the clipboard keeps (§16).
 *
 * The work itself, not a pointer to it, so a cut piece can still be pasted after it
 * has gone off the board, and work copied on page one can be pasted on page two.
 */
export interface Copied {
  /** Set when the piece is ink. */
  readonly stroke: Stroke | null;
  /** Set when the piece is an object: a shape, a text box, a sticky note. */
  readonly object: BoardObject | null;
}

/**
 * A copy of one piece of the board, already on it.
 *
 * A copy of ink is ink; a copy of a sticky note is a sticky note. Whoever made the
 * copy says which it was, and the view is how the selection holds it either way.
 */
export interface Duplicated extends Copied {
  /** The copy, as the selection holds it. */
  readonly view: Stroke;
}

/** The grips on the selection box. Corners resize; the one above it turns. */
export type HandleId = 'nw' | 'ne' | 'se' | 'sw' | 'rotate';

export interface Handle {
  readonly id: HandleId;
  /** Canvas coordinates. */
  readonly at: Point;
}

/** How far above the box the turn grip sits, in screen pixels. */
export const ROTATE_GAP_PX = 28;

/** How close a finger or pointer has to come to a grip to take hold of it. */
export const HANDLE_HIT_PX = 22;

/** How wide a grip is drawn, in screen pixels. */
export const HANDLE_SIZE_PX = 10;

/** Where a duplicate lands, in canvas units, so it does not hide the original. */
export const DUPLICATE_OFFSET = 16;

/** Scale is held inside these bounds so a selection can never be lost or exploded. */
const MIN_SCALE = 0.05;
const MAX_SCALE = 20;

/** The box around a set of strokes, ink width included, or null if there are none. */
export function selectionBounds(
  strokes: readonly Stroke[],
  boxOf: (stroke: Stroke) => Rect,
): Rect | null {
  return boundsOfRects(strokes.map(boxOf));
}

/** The grips on a selection box, at this zoom. */
export function handlesFor(bounds: Rect, zoom: number): Handle[] {
  const right = bounds.x + bounds.width;
  const bottom = bounds.y + bounds.height;
  return [
    { id: 'nw', at: { x: bounds.x, y: bounds.y } },
    { id: 'ne', at: { x: right, y: bounds.y } },
    { id: 'se', at: { x: right, y: bottom } },
    { id: 'sw', at: { x: bounds.x, y: bottom } },
    {
      id: 'rotate',
      at: { x: bounds.x + bounds.width / 2, y: bounds.y - ROTATE_GAP_PX / zoom },
    },
  ];
}

/**
 * How near the hand has to come to a grip to take hold of it, in canvas units.
 *
 * Generous, because a finger is not precise — but never so generous on a small
 * selection that the grips swallow the whole box. A few words picked out of a line
 * are far more often dragged somewhere than resized, and a teacher who could not pick
 * them up because every part of them was a resize grip would think the tool broken.
 */
export function grabReach(bounds: Rect, zoom: number): number {
  const generous = HANDLE_HIT_PX / zoom;
  const modest = Math.max(bounds.width, bounds.height) / 3;
  return Math.max(Math.min(generous, modest), 1 / zoom);
}

/** Which grip is under a point, if any. The nearest one wins. */
export function handleAt(point: Point, handles: readonly Handle[], reach: number): HandleId | null {
  let best: HandleId | null = null;
  let bestGap = reach;
  for (const handle of handles) {
    const gap = Math.hypot(point.x - handle.at.x, point.y - handle.at.y);
    if (gap > bestGap) continue;
    bestGap = gap;
    best = handle.id;
  }
  return best;
}

/** The corner a resize pivots about: the one opposite the grip being dragged. */
export function anchorFor(bounds: Rect, handle: HandleId): Point {
  const right = bounds.x + bounds.width;
  const bottom = bounds.y + bounds.height;
  if (handle === 'nw') return { x: right, y: bottom };
  if (handle === 'ne') return { x: bounds.x, y: bottom };
  if (handle === 'se') return { x: bounds.x, y: bounds.y };
  return { x: right, y: bounds.y };
}

export function centreOf(bounds: Rect): Point {
  return { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
}

/**
 * The change a resize drag describes.
 *
 * Always the same amount in both directions. Handwriting stretched more one way than
 * the other stops looking like handwriting, so the corner grips keep it in proportion.
 */
export function resizeMatrix(bounds: Rect, handle: HandleId, from: Point, to: Point): Matrix {
  const anchor = anchorFor(bounds, handle);
  const was = Math.hypot(from.x - anchor.x, from.y - anchor.y);
  if (was < 1e-6) return translation(0, 0);
  const now = Math.hypot(to.x - anchor.x, to.y - anchor.y);
  const scale = Math.min(Math.max(now / was, MIN_SCALE), MAX_SCALE);
  return scaleAbout(anchor, scale);
}

/** The change a turn drag describes: about the middle, by the angle the hand swept. */
export function rotateMatrix(bounds: Rect, from: Point, to: Point): Matrix {
  const centre = centreOf(bounds);
  const was = Math.atan2(from.y - centre.y, from.x - centre.x);
  const now = Math.atan2(to.y - centre.y, to.x - centre.x);
  return rotationAbout(centre, now - was);
}

/** Two changes as one, so a drag never has to be applied twice. */
export function combine(outer: Matrix, inner: Matrix): Matrix {
  return multiply(outer, inner);
}

/** The stroke as it would be after a change. Same id: it is the same work. */
export function transformStroke(stroke: Stroke, matrix: Matrix): Stroke {
  const points = strokePointsOf(stroke);
  const moved = new Float32Array(points.length * 3);
  points.forEach((point, index) => {
    const at = applyToPoint(matrix, point);
    moved[index * 3] = at.x;
    moved[index * 3 + 1] = at.y;
    moved[index * 3 + 2] = point.pressure;
  });
  return {
    ...stroke,
    points: moved,
    // Ink grows with the writing. A word scaled to twice the size is written twice as
    // thickly, exactly as it would be by hand.
    canvasWidth: stroke.canvasWidth * scaleFactorOf(matrix),
  };
}

/** A copy of a stroke, offset so it is visibly a second one. */
export function duplicateStroke(stroke: Stroke, offset = DUPLICATE_OFFSET): Stroke {
  const copy = transformStroke(stroke, translation(offset, offset));
  return { ...copy, id: newStrokeId(), createdAt: Date.now() };
}

/** The same stroke in a different colour. */
export function recolourStroke(stroke: Stroke, color: string): Stroke {
  return { ...stroke, color };
}

/** True when any part of the stroke lies inside the box. */
export function strokeInRect(stroke: Stroke, rect: Rect): boolean {
  return strokePointsOf(stroke).some((point) => pointInRect(point, rect));
}

/** True when any part of the stroke lies inside the loop the hand drew. */
export function strokeInPolygon(stroke: Stroke, polygon: readonly Point[]): boolean {
  if (polygon.length < 3) return false;
  return strokePointsOf(stroke).some((point) => pointInPolygon(point, polygon));
}
